import IMAGES from '@/assets/images';
import CustomImage from '@/components/CustomImage';
import { EyeIcon } from '@/icons';
import { Button, type TableProps } from 'antd';
import dayjs from 'dayjs';
import { useGetAllAttendanceQuery } from '../../../../app/features/employee/emp-attendance/emp-attendance.api';
import type {
  AttendanceData,
  AttendanceParams,
} from '../../../../app/features/employee/emp-attendance/emp-attendance.type';
import CustomTable from '../../../../components/CustomTable';
import { DB_DATE_FORMAT } from '../../../../constants/global.constants';
import useAuth from '../../../../hooks/useAuth';
import useError from '../../../../hooks/useError';
import useFilters from '../../../../hooks/useFilters';
import useModal from '../../../../hooks/useModal';
import { useQueryFilters } from '../../../../hooks/useQueryFilters';
import { addKeyInArray } from '../../../../utils/global.utils';
import AttendanceDetails from './_components/Summery/AttendanceDetails';
import PendingCorrections from './_components/Summery/PendingCorrections';

// Helper function to format time
const formatTime = (dateString: string | null) => {
  if (!dateString) return 'N/A';
  return dayjs(dateString).format('hh:mm A');
};

// Helper function to get status color
// const getStatusColor = (status: string | null) => {
//   switch (status) {
//     case "on_time":
//       return "green";
//     case "late":
//       return "red";
//     default:
//       return "default";
//   }
// };

// Helper function to format status text
const formatStatus = (status: string | null) => {
  if (!status) return 'N/A';
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export default function Summery() {
  const { organizationId } = useAuth();
  const {
    filter,
    limit,
    page,
    handleLimit,
    handlePagination,
    resetPagination,
  } = useFilters<AttendanceParams>();
  const { open, handleOpen, handleClose } = useModal<{ id: string }, 'VIEW'>();
  const { filters: queryFilter } = useQueryFilters<AttendanceParams>(
    {},
    resetPagination,
  );
  const { data, isLoading, isFetching, error } = useGetAllAttendanceQuery({
    params: {
      ...filter,
      ...queryFilter,
      organizationId,
    },
  });
  useError(error, 'Failed to fetch attendance data');

  const loading = isLoading || isFetching;

  const columns: TableProps<AttendanceData['data'][0]>['columns'] = [
    {
      title: 'ID',
      dataIndex: 'employee_id',
      align: 'center',
      render: (val: string) => val || 'N/A',
    },
    {
      title: 'Image',

      align: 'center',
      render: (record) => {
        return (
          <CustomImage
            size={40}
            alt={record?.name}
            className="mx-auto rounded-full object-cover"
            src={record?.photo || IMAGES.avatar}
          />
        );
      },
    },
    {
      title: 'Date',
      dataIndex: 'date',
      align: 'center',
      render: (val: string) =>
        val ? dayjs(val).format(DB_DATE_FORMAT) : 'N/A',
    },
    {
      title: 'Employee name',
      dataIndex: 'name',
      align: 'center',
      render: (val: string) => val ?? 'N/A',
    },
    {
      title: 'Check In',
      dataIndex: 'checkInTime',
      align: 'center',
      render: (val: string) => formatTime(val),
    },
    {
      title: (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          Offline Check In
        </div>
      ),
      dataIndex: 'checkInTime',
      width: 130,
      align: 'center',
      render: () => 'N/A', // This field is not in the API response
    },
    {
      title: 'Check Out',
      dataIndex: 'checkOutTime',
      align: 'center',
      render: (val: string | null) => formatTime(val),
    },
    {
      title: 'Status',
      dataIndex: 'checkInStatus',
      render: (val: string | null) => formatStatus(val),
    },
    {
      title: 'Actions',
      dataIndex: 'attendance_id',
      align: 'center',
      render: (id) => (
        <Button
          className="action-btn"
          size="small"
          icon={<EyeIcon size={20} />}
          onClick={() => handleOpen({ type: 'VIEW', data: id })}
        />
      ),
    },
  ];

  return (
    <>
      <CustomTable
        tableRootClassName="!border-0 !p-0"
        customTitle="Attendance List"
        columns={columns}
        dataSource={addKeyInArray(data?.data?.data)}
        hideRowSelect
        total={data?.data?.count}
        limit={limit}
        page={page}
        onLimitChange={handleLimit}
        onPaginate={handlePagination}
        loading={loading}
      />

      <PendingCorrections />

      {/* Leave Details Drawer */}
      <AttendanceDetails
        data={open.data}
        type={open.type}
        handleClose={handleClose}
        open={open.open && open.type === 'VIEW'}
      />
    </>
  );
}
